"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { FadeIn, HomeEyebrow, HomeHeading, HomeLead, HomeSection } from "~/components/siteglue/home/section-shell";
import { AuditDemoAnimation } from "~/components/siteglue/how-it-works/audit-demo-animation";
import { HOW_IT_WORKS_STEPS } from "~/components/siteglue/how-it-works/how-it-works-content";
import type { AuditDemoTabId } from "~/lib/siteglue/how-it-works/audit-demo";
import { cn } from "~/lib/utils";

export function HowItWorksSteps({
  variant = "page",
  className,
  audit,
}: {
  variant?: "teaser" | "page";
  setupHref?: string;
  className?: string;
  audit?: AuditDemoTabId;
}) {
  const isTeaser = variant === "teaser";
  const showAnimation = isTeaser || Boolean(audit);

  return (
    <HomeSection
      id="how-it-works"
      tone={isTeaser ? "white" : "slate"}
      className={cn(isTeaser && "py-16 sm:py-20", className)}
    >
      <FadeIn>
        <HomeEyebrow>How it works</HomeEyebrow>
        <HomeHeading className={isTeaser ? "mt-4 text-4xl font-semibold sm:text-5xl" : undefined}>
          Choose. Scan. Review. Improve. Verify.
        </HomeHeading>
        <HomeLead>
          Five steps from choosing an audit to verifying results — including the WordPress
          plugin for supported ARO fixes.
        </HomeLead>
      </FadeIn>

      {showAnimation ? (
        <FadeIn delay={0.08}>
          <AuditDemoAnimation className="mt-10" fixedTab={audit} />
        </FadeIn>
      ) : null}

      <ol className="mt-12 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {HOW_IT_WORKS_STEPS.map((step, index) => (
          <FadeIn key={step.id} delay={0.05 * (index + 1)}>
            <li className="flex h-full flex-col rounded-md border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#4f81bd]">
                {step.n} · {step.label}
              </p>
              <h3 className="mt-3 text-xl font-semibold text-slate-900">{step.heading}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{step.lead}</p>
              <Link
                href={step.cta.href}
                className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#4f81bd] hover:underline"
                data-testid={isTeaser ? `${step.cta.testId}-teaser` : step.cta.testId}
              >
                {step.cta.label}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </Link>
            </li>
          </FadeIn>
        ))}
      </ol>

      {isTeaser ? (
        <p className="mt-8 text-center">
          <Link
            href="/how-it-works"
            className="text-sm font-semibold text-slate-700 underline-offset-4 hover:underline"
          >
            See the full walkthrough
          </Link>
        </p>
      ) : null}
    </HomeSection>
  );
}
